import { useRef, useState } from "react";

interface Brand {
  id: number;
  name: string;
  logo: string;
}

const brands: Brand[] = [
  { id: 1, name: "Chatime", logo: "/images/Webp/Brand1.webp" },
  { id: 2, name: "Pho Maniac", logo: "/images/Webp/Brand2.webp" },
  { id: 3, name: "Dyson", logo: "/images/Webp/Brand3.webp" },
  { id: 4, name: "Pho Maniac", logo: "/images/Webp/Brand4.webp" },
  { id: 5, name: "Brothers Barbershop", logo: "/images/Webp/Brand5.webp" },
  { id: 6, name: "Brand 6", logo: "/images/Webp/Brand6.webp" },
  { id: 7, name: "Gong Cha", logo: "/images/Webp/Brand7.webp" },
  { id: 8, name: "East Coast Dog", logo: "/images/Webp/Brand8.webp" },
  { id: 9, name: "Chatime", logo: "/images/Webp/Brand9.webp" },
  { id: 10, name: "Dyson", logo: "/images/Webp/Brand10.webp" },
  { id: 11, name: "Dyson", logo: "/images/Webp/Brand11.webp" },
];

export default function BrandCarousel() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -280 : 280, behavior: "smooth" });
    setTimeout(updateScrollState, 350);
  };

  // Card size scales fluidly: 72px on tiny screens → 110px on wide screens
  const cardSize = "clamp(72px, 10vw, 150px)";
  // Logo image scales proportionally inside
  const logoMax = "clamp(44px, 6vw, 120px)";

  return (
    <section
      style={{
        width: "100%",
        padding: "clamp(16px, 3vw, 32px) clamp(8px, 2vw, 16px)",
        backgroundColor: "#fff",
        boxSizing: "border-box",
      }}
    >
      {/* Carousel wrapper */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* Left arrow */}
        <button
          onClick={() => scroll("left")}
          disabled={!canScrollLeft}
          aria-label="Scroll left"
          style={{
            flexShrink: 0,
            width: "clamp(28px, 4vw, 36px)",
            height: "clamp(28px, 4vw, 36px)",
            borderRadius: "50%",
            border: "1px solid #e5e7eb",
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#6b7280",
            cursor: canScrollLeft ? "pointer" : "not-allowed",
            opacity: canScrollLeft ? 1 : 0.3,
            transition: "background 0.2s",
            zIndex: 10,
            padding: 0,
          }}
        >
          <svg
            viewBox="0 0 24 24"
            style={{ width: "clamp(10px, 1.5vw, 16px)", height: "clamp(10px, 1.5vw, 16px)" }}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Scrollable track */}
        <div
          ref={scrollRef}
          onScroll={updateScrollState}
          style={{
            display: "flex",
            gap: "clamp(8px, 1.5vw, 24px)",
            overflowX: "auto",
            scrollBehavior: "smooth",
            margin: "0 clamp(4px, 1vw, 8px)",
            padding: "8px 0",
            flex: 1,
            scrollbarWidth: "none",
            // @ts-ignore
            msOverflowStyle: "none",
          }}
        >
          {brands.map((brand) => (
            <button
              key={brand.id}
              style={{
                flexShrink: 0,
                width: cardSize,
                height: cardSize,
                borderRadius: "50%",
                backgroundColor: "#fff",
                border: "1px solid #f3f4f6",
                boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "clamp(6px, 1vw, 16px)",
                cursor: "pointer",
                transition: "box-shadow 0.2s, border-color 0.2s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.boxShadow =
                  "0 4px 12px rgba(0,0,0,0.12)";
                (e.currentTarget as HTMLButtonElement).style.borderColor =
                  "#fca5a5";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.boxShadow =
                  "0 1px 3px rgba(0,0,0,0.07)";
                (e.currentTarget as HTMLButtonElement).style.borderColor =
                  "#f3f4f6";
              }}
            >
              <img
                src={brand.logo}
                alt={brand.name}
                style={{
                 
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </button>
          ))}
        </div>

        {/* Right arrow */}
        <button
          onClick={() => scroll("right")}
          disabled={!canScrollRight}
          aria-label="Scroll right"
          style={{
            flexShrink: 0,
            width: "clamp(28px, 4vw, 36px)",
            height: "clamp(28px, 4vw, 36px)",
            borderRadius: "50%",
            border: "1px solid #e5e7eb",
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#6b7280",
            cursor: canScrollRight ? "pointer" : "not-allowed",
            opacity: canScrollRight ? 1 : 0.3,
            transition: "background 0.2s",
            zIndex: 10,
            padding: 0,
          }}
        >
          <svg
            viewBox="0 0 24 24"
            style={{ width: "clamp(10px, 1.5vw, 16px)", height: "clamp(10px, 1.5vw, 16px)" }}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </section>
  );
}